import { useMemo } from "react";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";
import { useDomainStatus } from "./useDomainStatus";
import ICharityEmailSetup from "../../../../../../types/ICharityEmailSetup";

export interface DomainProgress {
  completedSteps: number;
  totalSteps: number;
  isLoaded: boolean;
}

export const useDomainProgress = (
  charityId: string,
  emailSetup: ICharityEmailSetup | null,
  emailSetupLoaded: boolean
): DomainProgress => {
  const domainStatus = useDomainStatus(emailSetup, emailSetupLoaded);

  return useMemo(() => {
    const totalSteps = 3;

    if (!emailSetupLoaded) {
      return {
        completedSteps: 0,
        totalSteps,
        isLoaded: false,
      };
    }

    let completedSteps = 0;

    if (domainStatus.hasNoDomain) {
      completedSteps = 0;
    } else if (domainStatus.hasUnverifiedDomain) {
      completedSteps = 1;
    } else if (domainStatus.hasVerifiedDomain && !emailSetup?.sendingEmailsConfigured) {
      completedSteps = 2;
    } else if (emailSetup?.sendingEmailsConfigured) {
      completedSteps = 3;
    }

    return {
      completedSteps,
      totalSteps,
      isLoaded: true,
    };
  }, [domainStatus, emailSetup, emailSetupLoaded]);
};

const withDomainProgress = (WrappedComponent: React.ComponentType<any>) => {
  const mapStateToProps = (state: any) => {
    const { user, firebase } = state;
    const charityId = user.currentCharity?.id || "";

    const emailSetupLoaded =
      charityId &&
      isLoaded(firebase.data[charityId]) &&
      isLoaded(get(firebase, `data.${charityId}.charitySetup.email`));

    const emailSetup: ICharityEmailSetup | null = emailSetupLoaded
      ? get(firebase, `data.${charityId}.charitySetup.email`, null)
      : null;

    return {
      charityId,
      emailSetup,
      emailSetupLoaded,
    };
  };

  return connect(mapStateToProps)(
    firebaseConnect((props: any, store: any) => {
      const charityId = store.getState().user.currentCharity?.id;
      return charityId ? [`${charityId}/charitySetup/email`] : [];
    })(WrappedComponent)
  );
};

export { withDomainProgress };